import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { MatPaginator, MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { PAGINATOR } from "../../constants/components";
import { DEFAULT_QUERY_PARAMS } from "../../constants/services";

export class ChangeEvent {
  page: number;
  pageSize: number;
  constructor(page: number, pageSize: number) {
    this.page = page;
    this.pageSize = pageSize;
  }
}

@Component({
  selector: "app-paginator",
  templateUrl: "./paginator.component.html",
  styleUrls: ["./paginator.component.css"],
})
export class PaginatorComponent implements AfterViewInit, AfterContentChecked {
  @Input() alias: string;
  @Input() disabled: boolean;
  @Input() labelHidden: boolean;
  @Input() syncWithQueryParams: boolean;
  @Input() length: number;
  @Input() page: number;
  @Input() pageSize: number;
  @Input() pageSizeOptions: number[];
  @Output() changeEvent = new EventEmitter<unknown>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  defaultPageSizeOptions: number[] = PAGINATOR.PAGE_SIZE_OPTIONS;

  constructor(private router: Router, private route: ActivatedRoute, private changeDetector: ChangeDetectorRef) {
    this.paginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  ngAfterViewInit(): void {
    this.mapQueryParamsToPaginator();
  }

  // #region public functions
  getPageParam(): string {
    return this.alias + "_page";
  }

  getPageSizeParam(): string {
    return this.alias + "_pageSize";
  }

  /** Get the page and the pageSize manually selected */
  getValues(): {
    page: number;
    pageSize: number;
  } {
    return {
      page: this.page || DEFAULT_QUERY_PARAMS.pagination.page,
      pageSize:
        this.pageSize ||
        this.pageSizeOptions?.[0] ||
        this.defaultPageSizeOptions?.[0] ||
        DEFAULT_QUERY_PARAMS.pagination.pageSize,
    };
  }

  getQueryParams(
    { page, pageSize }: { page: number; pageSize: number } = { page: undefined, pageSize: undefined },
  ): Params {
    this.page = page || this.page;
    this.pageSize = pageSize || this.pageSize;
    return {
      ...(this.page && { [this.getPageParam()]: this.page }),
      ...(this.pageSize && { [this.getPageSizeParam()]: this.pageSize }),
    };
  }

  goToFirstPage(): void {
    this.page = PAGINATOR.DEFAULT_PAGE;
    this.updateQueryParams();
  }

  clear(emitChange = true): void {
    const page = PAGINATOR.DEFAULT_PAGE;
    const pageSize = PAGINATOR.DEFAULT_PAGE_SIZE;
    this.page = page;
    this.pageSize = pageSize;
    if (this.syncWithQueryParams && emitChange) {
      this.updateQueryParams(page, pageSize);
    }
  }
  // #endregion

  // #region event handlers
  onPaginationChange(event: PageEvent): void {
    event.pageIndex++;
    const { pageIndex: page, pageSize } = event;
    this.page = page;
    this.pageSize = pageSize;

    this.updateQueryParams(page, pageSize);

    this.changeEvent.emit(new ChangeEvent(page, pageSize));
  }
  // #endregion

  // #region private functions
  private mapQueryParamsToPaginator(): void {
    if (!this.syncWithQueryParams || !location.search) {
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const value = Object.fromEntries(queryParams.entries());
    const page = Number(value[this.getPageParam()]) || PAGINATOR.DEFAULT_PAGE;
    const pageSize = Number(value[this.getPageSizeParam()]) || PAGINATOR.DEFAULT_PAGE_SIZE;
    this.page = page > PAGINATOR.DEFAULT_PAGE ? page : PAGINATOR.DEFAULT_PAGE;
    this.pageSize =
      pageSize > PAGINATOR.DEFAULT_PAGE_SIZE &&
      pageSize <= PAGINATOR.PAGE_SIZE_OPTIONS[PAGINATOR.PAGE_SIZE_OPTIONS.length - 1]
        ? pageSize
        : PAGINATOR.DEFAULT_PAGE_SIZE;
  }

  private updateQueryParams(page: number = this.page, pageSize: number = this.pageSize): void {
    if (!this.syncWithQueryParams) {
      return;
    }

    const queryParams: Params = {
      [this.getPageParam()]: page,
      [this.getPageSizeParam()]: pageSize,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: "merge",
    });
  }
  // #endregion
}
